import { Node } from '@tiptap/core'
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import { type ArticlePalette, buildPaletteElement, defaultPalettes, parsePaletteElement } from '@/lib/article-palette'
import { PaletteMatrix } from './PaletteMatrix'

/**
 * The colour palette as a single editor node.
 *
 * A node rather than hand-written markup inside `contenteditable`, because a colour
 * picker typed directly into the document is not practically editable: the author
 * would be typing inside the same element that carries the colour, and the
 * read-only path would have to guess which inputs were decorative.
 *
 * `atom: true` makes the whole block one unit to the editor — selectable,
 * deletable and movable as a piece, with its interior owned by the node view
 * instead of by ProseMirror's cursor.
 */

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		articlePalette: {
			/** Insert a palette block at the cursor, seeded with the template's palettes. */
			insertArticlePalette: () => ReturnType
		}
	}
}

/** The node view: the editable matrix, writing every change back as a node attribute. */
function PaletteNodeView({ node, updateAttributes, deleteNode, editor }: NodeViewProps) {
	const palettes = (node.attrs.palettes ?? []) as ArticlePalette[]

	return (
		<NodeViewWrapper
			// `contentEditable={false}` keeps ProseMirror's cursor out of the controls:
			// without it, clicking a hex field puts the caret in the document instead.
			contentEditable={false}
			className="my-4 rounded-ds-card border border-ds-border bg-ds-surface p-4"
			data-testid="article-palette-block"
		>
			<div className="mb-3 flex items-center justify-between">
				<p className="font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle">Colour palettes</p>
				{editor.isEditable ? (
					<button type="button" onClick={deleteNode} className="text-xs text-ds-text-muted hover:text-ds-danger-strong">
						Remove block
					</button>
				) : null}
			</div>
			<PaletteMatrix
				palettes={palettes}
				onChange={editor.isEditable ? (next) => updateAttributes({ palettes: next }) : undefined}
			/>
		</NodeViewWrapper>
	)
}

export const ArticlePaletteNode = Node.create({
	name: 'articlePalette',
	group: 'block',
	atom: true,
	draggable: true,

	addAttributes() {
		return {
			palettes: {
				default: [] as ArticlePalette[],
				// Read straight from the stored markup, which is self-describing: every
				// palette name and swatch colour is reconstructible without a schema.
				parseHTML: (element) => parsePaletteElement(element),
				// Carried in the rendered DOM rather than as an attribute of its own —
				// `renderHTML` below builds the block from this value.
				renderHTML: () => ({}),
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-article-palette]' }]
	},

	/**
	 * Returns a real DOM element from the shared builder, so `editor.getHTML()`
	 * produces byte-identical markup to the template's own serialization. A second
	 * hand-written copy of the format here is exactly how stored articles stop
	 * parsing.
	 */
	renderHTML({ node }) {
		return buildPaletteElement((node.attrs.palettes ?? []) as ArticlePalette[])
	},

	addNodeView() {
		return ReactNodeViewRenderer(PaletteNodeView)
	},

	addCommands() {
		return {
			insertArticlePalette:
				() =>
				({ commands }) =>
					commands.insertContent({ type: this.name, attrs: { palettes: defaultPalettes() } }),
		}
	},
})
